import React, { useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, Save, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Math.round(n || 0).toLocaleString("it-IT");

export default function EditFatturaModal({ fattura, onClose, onSaved }) {
  const [items, setItems] = useState(() =>
    (fattura.items || []).map((it, i) => ({ ...it, _uid: `it-${Date.now()}-${i}` }))
  );
  const [vehicleName, setVehicleName] = useState(fattura.vehicle_name || "");
  const [saving, setSaving] = useState(false);

  const total = useMemo(() => items.reduce((s, i) => s + (parseFloat(i.subtotal) || 0), 0), [items]);
  const activityTotal = total; // per semplicità = total (il concetto attività è stato rimosso)
  const commission = Math.round(total * (fattura.percent || 0));

  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    // recompute subtotal
    const qty = parseInt(next[idx].qty || 0, 10) || 0;
    const unit = parseFloat(next[idx].unit_price || 0) || 0;
    next[idx].subtotal = qty * unit;
    setItems(next);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    setItems([...items, { _uid: `it-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, key: "custom", label: "Nuova voce", qty: 1, unit_price: 0, subtotal: 0 }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanItems = items.map(({ _uid, ...rest }) => rest);
      await api.patch(`/fatture/${fattura.id}`, {
        items: cleanItems,
        vehicle_name: vehicleName || null,
        total,
        activity_total: activityTotal,
      });
      toast.success("Fattura aggiornata · Stipendio ricalcolato");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-fattura-modal">
      <div className="ot-panel-elev max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0a1024] border-b border-blue-400/20 p-5 flex items-center justify-between">
          <div>
            <div className="ot-label">// MODIFICA FATTURA</div>
            <h2 className="ot-heading text-2xl font-black uppercase tracking-tight text-slate-100">
              {fattura.display_name} · {new Date(fattura.created_at).toLocaleDateString("it-IT")}
            </h2>
            <div className="ot-mono text-[0.65rem] text-blue-400/80 tracking-widest mt-0.5">
              Ruolo {fattura.role} · {Math.round((fattura.percent || 0) * 100)}%{fattura.paid_at ? " · GIÀ PAGATA (attenzione)" : ""}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="ot-label block mb-1.5">Veicolo (opzionale)</label>
            <input data-testid="edit-vehicle-name" className="ot-input" value={vehicleName} onChange={(e) => setVehicleName(e.target.value)} placeholder="Es. Pegassi Zentorno" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="ot-label">Voci fattura</div>
              <button onClick={addItem} data-testid="add-item" className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1 text-xs">
                <Plus className="w-3 h-3" /> Aggiungi voce
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={it._uid} className="ot-panel p-3 grid grid-cols-12 gap-2 items-center" data-testid={`edit-item-${idx}`}>
                  <input
                    className="ot-input col-span-5 text-sm"
                    value={it.label}
                    onChange={(e) => updateItem(idx, { label: e.target.value })}
                    placeholder="Descrizione"
                  />
                  <input
                    className="ot-input col-span-2 ot-mono text-sm"
                    type="number"
                    min="0"
                    value={it.qty}
                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                    placeholder="Qtà"
                  />
                  <input
                    className="ot-input col-span-3 ot-mono text-sm"
                    type="number"
                    min="0"
                    value={it.unit_price}
                    onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                    placeholder="Prezzo"
                  />
                  <div className="col-span-1 ot-mono text-sm text-blue-400 text-right font-bold">${fmt(it.subtotal)}</div>
                  <button onClick={() => removeItem(idx)} className="col-span-1 text-red-400 hover:text-red-300 flex justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-center py-6 text-slate-500 text-sm">Nessuna voce · aggiungi almeno una riga</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="ot-panel p-4">
              <div className="ot-label mb-1">NUOVO TOTALE</div>
              <div className="ot-mono text-3xl font-black text-blue-400" data-testid="edit-total">$ {fmt(total)}</div>
            </div>
            <div className="ot-panel p-4">
              <div className="ot-label mb-1">STIPENDIO RICALCOLATO</div>
              <div className="ot-mono text-3xl font-black text-amber-400" data-testid="edit-commission">$ {fmt(commission)}</div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#0a1024] border-t border-blue-400/20 p-5 flex gap-3">
          <button onClick={save} disabled={saving} className="ot-btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-edit-fattura">
            <Save className="w-4 h-4" />
            {saving ? "Salvo..." : "Salva Modifiche"}
          </button>
          <button onClick={onClose} className="ot-btn-ghost">Annulla</button>
        </div>
      </div>
    </div>
  );
}
